import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { Sort } from "@/components/atoms";
import usePaginationFromEndpoint, {
    usePaginationFromEndpointProps,
} from "@/components/templates/PaginatedList/usePaginationFromEndpoint";
import useSearchFromEndpoint, {
    useSearchFromEndpointProps,
} from "@/components/templates/SearchList/useSearchFromEndpoint";
import { FieldPath } from "@/core/fieldPath";
import { invoiceApi } from "@/shared/apis";
import { InvoicesQueryParameters, searchableInvoiceMapper } from "@/shared/apis/invoiceApi";
import { InvoiceSortProperty, SearchableInvoice, SortOrder } from "@/shared/domain";
import { useGlobalFilter } from "@/shared/hooks";
import { usePolicyholders } from "@/shared/hooks/usePolicyholders";

type useInvoiceQueriesProps = {
    tableMode: boolean;
    useInvoicesPageQuery: ({
        defaultCategory,
    }) => usePaginationFromEndpointProps<MyBrokerApi.Invoice, InvoiceSortProperty>;
    useInvoicesSearchQuery: ({ textSearch, sortProperty }) => useSearchFromEndpointProps<MyBrokerApi.Invoice>;
};

const defaultSorting: Sort<InvoiceSortProperty> = {
    property: "reference",
    order: SortOrder.Descending,
};

export const getSearchableInvoiceFilters = (withPolicyholder: boolean): FieldPath<SearchableInvoice>[] => {
    const filters: FieldPath<SearchableInvoice>[] = ["dateString", "reference", "branch.name", "amount"];
    filters.pushIf(withPolicyholder, "policyHolder");

    return filters;
};

const useInvoicesQueries = (): useInvoiceQueriesProps => {
    const { isProEnvironment } = useGlobalFilter();
    const { policyholders } = usePolicyholders({
        checkRight: (a) => a.canViewInvoices,
    });
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const tableMode = isProEnvironment && !isSmallScreen;

    const useInvoicesPageQuery = ({ defaultCategory }: { defaultCategory: MyBrokerApi.InvoiceStatus }) => {
        return usePaginationFromEndpoint<
            MyBrokerApi.Invoice,
            MyBrokerApi.InvoicePayload,
            InvoicesQueryParameters,
            InvoiceSortProperty
        >({
            objectType: "invoice",
            policyholdersIdList: policyholders,
            endPoint: invoiceApi.getInvoices,
            defaultCategory,
            tableMode,
            defaultSorting,
        });
    };

    const useInvoicesSearchQuery = ({ textSearch, sortProperty }) => {
        const filters = getSearchableInvoiceFilters(!isProEnvironment);

        const filterInvoices = (results: MyBrokerApi.Invoice[]) =>
            searchableInvoiceMapper(results).filterBy(textSearch, filters).sortBy(sortProperty);

        return useSearchFromEndpoint<MyBrokerApi.Invoice, MyBrokerApi.InvoicePayload, InvoicesQueryParameters>(
            "invoices",
            invoiceApi.getInvoicesLazy,
            policyholders,
            filterInvoices,
        );
    };

    return {
        tableMode,
        useInvoicesPageQuery,
        useInvoicesSearchQuery,
    };
};

export default useInvoicesQueries;
